import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReusableModal from "../../ManageSettings/ReusableModal";
import ManageStockController from "../ManageStock/ManageStockController";
import StockHistoryController from "../ManageStock/StockHistoryController";
import scanner from "../../../../../assets/Image/scan.png";
import "./Inventory.css";
import { ToastContainer } from "react-toastify";
import stockIcon from "../../../../../assets/Image/add-stock (black).png";
import HistoryIcon from "../../../../../assets/Image/stock-history (light).png";
import AddVendorModal from "../../ReusableForm/AddVendorModal";
import PaginatedSearchSelect from "../../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import PaginatedMultiSelect from "../../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedMultiSelect";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import config from "../../../../../config/Global.json";
import {
  TextField,
  FormControl,
  Autocomplete,
  MenuItem,
} from "../../../../common/ReusableComponent/MUIComponents";
import {
  CustomButton,
  RegularButton,
} from "../../../../common/ReusableComponent/CustomButton";
import {
  Search,
  Package,
  Boxes,
  ChevronDown,
  History,
  AlertTriangle,
  Layers,
  Wallet,
  TrendingUp,
  Archive,
  Truck,
} from "lucide-react";

const safeNumber = (value) => {
  if (value === "-" || value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => safeNumber(value).toLocaleString("en-IN");

const formatMoney = (value) =>
  safeNumber(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date
    .toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\bam\b/i, "AM")
    .replace(/\bpm\b/i, "PM");
};

const getMediaApiHost = () => {
  const configuredHost = String(config?.api?.host || "").trim();

  if (!configuredHost) {
    return "";
  }

  /*
   * Product module reference:
   * /media/... should become https://api.paisavara.com/media/...
   *
   * If config.api.host is already https://api.paisavara.com, keep it.
   * If config.api.host is https://api.paisavara.com/api or /api/v1,
   * strip the API path before attaching /media.
   */
  return configuredHost
    .replace(/\/api\/v\d+\/?$/i, "")
    .replace(/\/api\/?$/i, "")
    .replace(/\/$/, "");
};

const resolveMediaUrl = (value) => {
  if (!value) return "";

  const mediaPath = String(value).trim();
  if (!mediaPath) return "";

  if (
    mediaPath.startsWith("http://") ||
    mediaPath.startsWith("https://") ||
    mediaPath.startsWith("data:") ||
    mediaPath.startsWith("blob:")
  ) {
    return mediaPath;
  }

  if (mediaPath.startsWith("/media/")) {
    const mediaApiHost = getMediaApiHost();
    return mediaApiHost ? `${mediaApiHost}${mediaPath}` : mediaPath;
  }

  if (mediaPath.startsWith("/")) {
    const mediaApiHost = getMediaApiHost();
    return mediaApiHost ? `${mediaApiHost}${mediaPath}` : mediaPath;
  }

  return mediaPath;
};

// Example: "/media/.../1.webp" => `${getMediaApiHost()}/media/.../1.webp`
const getProductImage = (item) => {
  const source = item?.__sourceProduct || item || {};

  const directImage =
    source.product_image ||
    source.productImage ||
    source.image ||
    source.thumbnail ||
    source.thumbnail_image ||
    source.product_thumbnail ||
    source.photo ||
    item?.product_image ||
    item?.image ||
    item?.thumbnail;

  if (directImage) return resolveMediaUrl(directImage);

  const gallery =
    source.media_gallery ||
    source.mediaGallery ||
    source.gallery ||
    source.images ||
    item?.media_gallery ||
    item?.gallery ||
    [];

  if (Array.isArray(gallery) && gallery.length > 0) {
    const firstImage = gallery.find(Boolean);

    if (typeof firstImage === "string") {
      return resolveMediaUrl(firstImage);
    }

    if (firstImage && typeof firstImage === "object") {
      return resolveMediaUrl(
        firstImage.image ||
          firstImage.url ||
          firstImage.file ||
          firstImage.path ||
          firstImage.media
      );
    }
  }

  return "";
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const getProductTitle = (item) =>
  item?.title ||
  item?.product_title ||
  item?.product_name ||
  item?.productTitle ||
  item?.combinedField ||
  "Untitled Product";

const getHistoryTitle = (item) =>
  item?.title ||
  item?.product_title ||
  item?.product_name ||
  item?.productTitle ||
  item?.combinedField ||
  "Untitled Product";

const pickFirstValue = (item, keys = []) => {
  if (!item || typeof item !== "object") return "";

  for (const key of keys) {
    const value = item[key];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
};

const getNestedValue = (item, paths = []) => {
  if (!item || typeof item !== "object") return "";

  for (const path of paths) {
    const value = path.split(".").reduce((current, key) => {
      if (current === undefined || current === null) return undefined;
      return current[key];
    }, item);

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
};

const getCategoryNames = (item) => {
  const categories = [];

  const addCategory = (value) => {
    if (!value) return;

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed && !categories.includes(trimmed)) categories.push(trimmed);
      return;
    }

    if (typeof value === "object") {
      const name = value.name || value.title || value.category_name || value.label;
      if (name && !categories.includes(name)) categories.push(name);
    }
  };

  addCategory(item?.category);
  addCategory(item?.category_name);
  addCategory(item?.product_category);
  addCategory(item?.categoryName);

  if (Array.isArray(item?.categories?.assigned)) {
    item.categories.assigned.forEach(addCategory);
  }

  if (Array.isArray(item?.categories?.breadcrumb)) {
    item.categories.breadcrumb.flat(Infinity).forEach(addCategory);
  }

  if (Array.isArray(item?.assigned_categories)) {
    item.assigned_categories.forEach(addCategory);
  }

  if (Array.isArray(item?.category_list)) {
    item.category_list.forEach(addCategory);
  }

  return categories.length > 0 ? categories : ["Uncategorized"];
};

const getProductCategory = (item) => getCategoryNames(item)[0];

const normalizeSegmentVariant = (product, segment = {}) => ({
  ...product,
  ...segment,
  __sourceProduct: product,
});

const getItemVariants = (item) => {
  const segmentSources = [
    item?.price_info,
    item?.priceInfo,
    item?.segments,
    item?.segment_data,
    item?.inventory_segments,
    item?.uom_prices,
    item?.uomData,
    item?.uom_data,
    item?.product_segments,
    item?.stock_segments,
    item?.inventory_data,
  ];

  const segmentArray = segmentSources.find(
    (source) => Array.isArray(source) && source.length > 0
  );

  if (segmentArray) {
    return segmentArray.map((segment) => normalizeSegmentVariant(item, segment));
  }

  return [normalizeSegmentVariant(item)];
};

const getUomTitle = (item) => {
  const directValue = pickFirstValue(item, [
    "UOM",
    "uom_title",
    "uom_name",
    "unit_name",
    "unit",
    "segment",
    "segment_name",
    "uom_display",
  ]);

  if (directValue) return directValue;

  const nestedValue = getNestedValue(item, [
    "uom.title",
    "uom.name",
    "uom.code",
    "uom.label",
    "unit.title",
    "unit.name",
    "selected_uom.title",
    "selected_uom.name",
    "uom_data.title",
    "uom_data.name",
    "uomData.title",
    "uomData.name",
  ]);

  if (nestedValue) return nestedValue;

  const sourceProduct = item?.__sourceProduct;
  const fallbackValue = pickFirstValue(sourceProduct, [
    "UOM",
    "uom_title",
    "uom_name",
    "unit_name",
    "unit",
  ]);

  return fallbackValue || "-";
};

const getRemainingStock = (item) => {
  const value = pickFirstValue(item, [
    "remaining_inventory",
    "current_inventory",
    "current_stock",
    "total_current_stock",
    "stock",
    "available_stock",
    "available_inventory",
    "inventory",
    "inventory_qty",
    "quantity_available",
    "balance_stock",
    "opening_inventory",
  ]);

  if (value === "-") return 0;
  if (value !== "") return safeNumber(value);

  const nestedValue = getNestedValue(item, [
    "inventory.remaining_inventory",
    "inventory.current_stock",
    "stock.remaining_inventory",
    "stock.current_stock",
    "segment.remaining_inventory",
  ]);

  if (nestedValue === "-") return 0;
  if (nestedValue !== "") return safeNumber(nestedValue);

  return 0;
};

const getPurchasePrice = (item) => {
  const value = pickFirstValue(item, [
    "purchasePrice",
    "purchase_price",
    "purchaseprice",
    "purchase_price_per_pc",
    "purchasePricePerPc",
    "cost_price",
    "buying_price",
    "base_purchase_price",
  ]);

  if (value !== "") return safeNumber(value);

  const nestedValue = getNestedValue(item, [
    "price_info.0.purchase_price",
    "price_info.0.purchasePrice",
    "pricing.purchase_price",
    "price.purchase_price",
    "segment.purchase_price",
  ]);

  return safeNumber(nestedValue);
};

const getSellingPrice = (item) => {
  const value = pickFirstValue(item, [
    "sellingPrice",
    "selling_price",
    "sellingprice",
    "selling_price_per_pc",
    "sellingPricePerPc",
    "sale_price",
    "sales_price",
    "mrp",
    "retail_price",
    "base_selling_price",
  ]);

  if (value !== "") return safeNumber(value);

  const nestedValue = getNestedValue(item, [
    "price_info.0.selling_price",
    "price_info.0.sellingPrice",
    "pricing.selling_price",
    "price.selling_price",
    "segment.selling_price",
  ]);

  return safeNumber(nestedValue);
};

const getStockStatus = (stock) => {
  const current = safeNumber(stock);

  if (current <= 0) {
    return { label: "No Stock", className: "danger" };
  }

  if (current <= 20) {
    return { label: "Low", className: "warning" };
  }

  if (current <= 100) {
    return { label: "Watch", className: "info" };
  }

  return { label: "Healthy", className: "success" };
};

const MiniMetric = ({ icon: Icon, label, value, tone = "default" }) => (
  <div className={`invx-mini-metric ${tone}`}>
    <span className="invx-mini-icon">
      <Icon size={16} />
    </span>
    <div>
      <strong>{value}</strong>
      <p>{label}</p>
    </div>
  </div>
);

const StockBadge = ({ value }) => {
  const status = getStockStatus(value);

  return (
    <span className={`invx-stock-badge ${status.className}`}>
      {status.label}
    </span>
  );
};

const ProductAccordionRow = ({ group, expanded, onToggle }) => (
  <div className={`invx-product-row ${expanded ? "expanded" : ""}`}>
    <button type="button" className="invx-product-main" onClick={onToggle}>
      <span className="invx-product-left">
        <span className={`invx-product-avatar ${group.imageUrl ? "has-image" : ""}`}>
          {group.imageUrl && (
            <img
              src={group.imageUrl}
              alt={group.title}
              onError={(event) => {
                event.currentTarget.style.display = "none";
                event.currentTarget.parentElement?.classList.remove("has-image");
              }}
            />
          )}
          <Package className="invx-placeholder-icon" size={21} />
        </span>

        <span className="invx-product-text">
          <strong>{group.title}</strong>
          <span>
            {group.categories.join(", ")} • {group.variants.length} UOM / price segment{group.variants.length > 1 ? "s" : ""}
          </span>
        </span>
      </span>

      <span className="invx-product-metrics">
        <span>
          <small>Total Stock</small>
          <b>{formatCount(group.totalStock)}</b>
        </span>

        <span>
          <small>Stock Value</small>
          <b>₹{formatMoney(group.stockValue)}</b>
        </span>

        <StockBadge value={group.totalStock} />

        <ChevronDown className="invx-chevron" size={20} />
      </span>
    </button>

    {expanded && (
      <div className="invx-expanded-area">
        <div className="invx-expanded-grid">
          <div className="invx-detail-card">
            <div className="invx-detail-title">
              <Layers size={16} />
              Stock by UOM
            </div>

            <div className="invx-variant-table-wrap">
              <table className="invx-variant-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>UOM</th>
                    <th>Current</th>
                    <th>Purchase</th>
                    <th>Selling</th>
                    <th>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {group.variants.map((variant, index) => {
                    const purchase = getPurchasePrice(variant);
                    const selling = getSellingPrice(variant);
                    const margin = selling - purchase;

                    return (
                      <tr key={`${group.key}-variant-${index}`}>
                        <td>{getCategoryNames(variant).join(", ")}</td>
                        <td>{getUomTitle(variant)}</td>
                        <td>
                          <span className="invx-qty">{formatCount(getRemainingStock(variant))}</span>
                        </td>
                        <td>₹{formatMoney(purchase)}</td>
                        <td>₹{formatMoney(selling)}</td>
                        <td className={margin >= 0 ? "invx-positive" : "invx-negative"}>
                          ₹{formatMoney(margin)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="invx-detail-card">
            <div className="invx-detail-title">
              <History size={16} />
              Recent Stock Movement
            </div>

            {group.history.length > 0 ? (
              <div className="invx-history-list">
                {group.history.slice(0, 5).map((entry, index) => (
                  <div className="invx-history-item" key={`${group.key}-history-${index}`}>
                    <div>
                      <strong>{entry.vendor_name || "No Vendor"}</strong>
                      <span>{formatDateTime(entry.date)} • {entry.segment_status || "Stock Entry"}</span>
                    </div>
                    <b>+{formatCount(entry.total_stock_arrived || entry.current_stock || 0)}</b>
                  </div>
                ))}
              </div>
            ) : (
              <div className="invx-empty-history">
                No stock history found for this product on current page.
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
);

const Inventory = () => {
  const {
    showModal,
    handleOpenModal,
    handleCloseModal,
    productList,
    selectedProduct,
    barcode,
    category,
    purchasePrice,
    sellingPrice,
    handleOpeningInventoryChange,
    currentStock,
    totalStockArrived,
    isNewProduct,
    showConfirmation,
    productTitle,
    openingInventory,
    inventoryData,
    setSelectedProduct,
    setTotalStockArrived,
    handleSubmit,
    handleConfirmNewProduct,
    handleCancelNewProduct,
    scanBarcode,
    handleBarcodeChange,
    handleCategoryChange,
    handleProductTitleChange,
    handleUomChange,
    purchasePriceError,
    sellingPriceError,
    handlePurchasePriceBlur,
    handleSellingPriceBlur,
    handlePurchasePriceChange,
    handleSellingPriceChange,
    vendors,
    selectedVendorId,
    showVendorModal,
    handleVendorChange,
    handleCloseVendorModal,
    updateVendors,
    handleVendorAdded,
    totalPcInUom,
    handleTotalPcInUomChange,
    segments,
    selectedSegment,
    resetForm,
    uomDataStore,
    currentPage,
    totalCount,
    loading,
    isSubmitting,
    handlePageChange,
    handleDataUpdate,
    setSearchQuery,
    fetchProductList,
    setProductTitle,
    selectedUom,
    setSelectedUom,
    handleFocusOpeningInventory,
    handleBlurOpeningInventory,
    searchTerm,
    handleSearch,
    selectedCategory,
    setSelectedCategory,
    autoGenerateBarcode,
    setAutoGenerateBarcode,
    selectedVendorName,
    setSelectedVendorName,
  } = ManageStockController();

  const {
    stockData = [],
    loading: stockHistoryLoading,
  } = StockHistoryController();

  const [expandedProductKey, setExpandedProductKey] = useState(null);
  const [localSearch, setLocalSearch] = useState("");


  const { GetPaginatedData } = useApi();




  const navigate = useNavigate();
  const handleManageStockClick = () => {
    navigate("/stock-history");
  };

  const groupedProducts = useMemo(() => {
    const inventoryRows = Array.isArray(inventoryData) ? inventoryData : [];
    const historyRows = Array.isArray(stockData) ? stockData : [];
    const productMap = new Map();

    inventoryRows.forEach((item) => {
      const title = getProductTitle(item);
      const key = normalizeText(title);
      const categories = getCategoryNames(item);
      const variants = getItemVariants(item);

      if (!productMap.has(key)) {
        productMap.set(key, {
          key,
          title,
          categories: new Set(),
          variants: [],
          history: [],
        });
      }

      const product = productMap.get(key);
      categories.forEach((categoryName) => product.categories.add(categoryName));
      product.variants.push(...variants);
    });

    historyRows.forEach((entry) => {
      const historyTitle = getHistoryTitle(entry);
      const historyKey = normalizeText(historyTitle);

      let matchingKey = historyKey;

      if (!productMap.has(matchingKey)) {
        matchingKey =
          Array.from(productMap.keys()).find((key) => {
            if (!key || !historyKey) return false;
            return key.includes(historyKey) || historyKey.includes(key);
          }) || historyKey;
      }

      if (!productMap.has(matchingKey)) {
        productMap.set(matchingKey, {
          key: matchingKey,
          title: historyTitle,
          categories: new Set(["Uncategorized"]),
          variants: [],
          history: [],
        });
      }

      productMap.get(matchingKey).history.push(entry);
    });

    return Array.from(productMap.values())
      .map((product) => {
        const totalStock = product.variants.reduce(
          (sum, item) => sum + getRemainingStock(item),
          0
        );

        const stockValue = product.variants.reduce(
          (sum, item) => sum + getRemainingStock(item) * getSellingPrice(item),
          0
        );

        return {
          ...product,
          categories: Array.from(product.categories),
          totalStock,
          stockValue,
          imageUrl: getProductImage(product.variants[0]),
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [inventoryData, stockData]);

  const filteredProducts = useMemo(() => {
    const query = normalizeText(localSearch);

    if (!query) return groupedProducts;

    return groupedProducts.filter((product) => {
      const searchable = [
        product.title,
        product.categories.join(" "),
        ...product.variants.map((variant) => getUomTitle(variant)),
        ...product.history.map((entry) => `${entry.vendor_name || ""} ${entry.segment_status || ""}`),
      ]
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [groupedProducts, localSearch]);

  const totalProducts = groupedProducts.length;
  const totalCategories = new Set(
    groupedProducts.flatMap((product) => product.categories)
  ).size;
  const totalInventoryUnits = groupedProducts.reduce(
    (sum, product) => sum + product.totalStock,
    0
  );
  const totalInventoryValue = groupedProducts.reduce(
    (sum, product) => sum + product.stockValue,
    0
  );
  const lowStockCount = groupedProducts.filter(
    (product) => product.totalStock > 0 && product.totalStock <= 20
  ).length;
  const noStockCount = groupedProducts.filter(
    (product) => product.totalStock <= 0
  ).length;

  const isInventoryLoading = loading || stockHistoryLoading;

  return (
    <>
      <ToastContainer />
      <style>{inventoryGroupedStyles}</style>
      <div className="responsive-box invx-page">
        <div className="inventory-header">
          <div className="inventory-title">Inventory</div>
          <div
            className="btn-position"
            style={{ display: "flex", gap: "10px" }}
          >
            <CustomButton
              text="Stock History"
              icon={HistoryIcon}
              onClick={handleManageStockClick}
              color="#FFFFFF"
              backgroundColor="#4a5546"
              iconPosition="right"
            />
            <CustomButton
              text="Add Stock"
              icon={stockIcon}
              onClick={handleOpenModal}
              color="#000000"
              backgroundColor="#FFAF32"
              iconPosition="right"
            />
          </div>
        </div>
        <ReusableModal
          title={isNewProduct ? "Add Product" : "Add Stock"}
          show={showModal}
          handleClose={handleCloseModal}
          width="550px"
          height="580px"
        >
          <div className="">
            {/* Barcode Scanner Section */}
            <div className="product-form-fields">
              <div
                className="form-group"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={scanner}
                  alt="Barcode Icon"
                  style={{ marginRight: "10px" }}
                />
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    className="form-control-sales"
                    name="barcode"
                    placeholder=""
                    value={barcode}
                    onChange={handleBarcodeChange}
                    onBlurCapture={scanBarcode}
                    style={{ paddingLeft: "10px", marginLeft: "0" }}
                  />
                  <label className="floating-label">Barcode</label>
                </div>
              </div>
            </div>

            {showConfirmation && (
              <div
                className="product-not-found"
                style={{
                  margin: "10px 0",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "space-beetween",
                }}
              >
                <span
                  style={{
                    color: "#00000",
                    fontSize: "14px",
                    fontWeight: "700px",
                  }}
                >
                  <strong>
                    Do you want to add it as new product with opening stock?
                  </strong>
                </span>

                <input
                  type="checkbox"
                  checked={isNewProduct}
                  onChange={() => {
                    if (!isNewProduct) {
                      handleCloseModal();
                      navigate("/product", { state: { openAddModal: true, barcode } });
                    } else {
                      handleCancelNewProduct();
                    }
                  }}
                  style={{
                    transform: "scale(1.2)",
                    accentColor: "black",
                  }}
                />
              </div>
            )}

            {!isNewProduct && (
              // <div className="product-form">
              <div className="product-form-row">
                <div className="form-group product-field">
                  <FormControl fullWidth variant="outlined" size="small">
                    <Autocomplete
                      value={
                        selectedProduct
                          ? typeof selectedProduct === "object"
                            ? selectedProduct
                            : { id: selectedProduct, title: productTitle }
                          : null
                      }
                      onChange={(event, newValue) => {
                        setSelectedProduct(newValue);
                        setProductTitle(newValue?.title || "");
                      }}
                      onInputChange={(event, newInputValue) => {
                        setSearchQuery(newInputValue);
                      }}
                      loading={loading}
                      options={productList}
                      getOptionLabel={(option) => option?.title || ""}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return false;
                        const optionId = option.id;
                        const valueId =
                          typeof value === "object" ? value.id : value;
                        return optionId === valueId;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Product"
                          variant="standard"
                          className="advance-input"
                         
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: false,
                          
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <MenuItem {...props}>{option.title}</MenuItem>
                      )}
                      ListboxProps={{
                        sx: { maxHeight: 200, overflow: "auto" },
                      }}
                    />
                  </FormControl>
                </div>
                <div
                  className="form-group product-field"
                  style={{ marginTop: "24px" }}
                >
                  <input
                    type="number"
                    className="form-control-sales"
                    name="total_stock_arrived"
                    value={totalStockArrived}
                    onChange={(e) => setTotalStockArrived(e.target.value)}
                    placeholder=""
                  />
                  <label className="floating-label">Addition In Stock</label>
                </div>
              </div>
              // </div>
            )}

            {isNewProduct && (
              <div className="product-form-container">
                <div className="product-form-row">
                  <div className="form-group product-field" style={{ marginTop: "18px" }}>
                    <input
                      type="text"
                      className="form-control-sales"
                      value={productTitle}
                      onChange={handleProductTitleChange}
                      placeholder=""
                    />
                    <label className="floating-label">Product Title</label>
                  </div>
                  <div className="form-group product-field" style={{ marginTop: "18px" }}>
                    <PaginatedMultiSelect
                      api="productCategory"
                      GetPaginatedData={GetPaginatedData}
                      getOptionLabel={(opt) => opt.name}
                      value={selectedCategory}
                      onSelect={setSelectedCategory}
                      placeholder="Select Category"
                    />
                  </div>
                </div>
                <div className="product-form-row">
                  <div className="form-group product-field">
                    <PaginatedSearchSelect
                      api="uomData"
                      GetPaginatedData={GetPaginatedData}
                      getOptionLabel={(opt) => opt.uom_title}
                      onSelect={(opt) => setSelectedUom(opt)}
                      placeholder="Select UOM"
                      value={selectedUom}
                    />
                  </div>
                  <div className="form-group product-field">
                    <PaginatedSearchSelect
                      api="vendorDepartmentData"
                      GetPaginatedData={GetPaginatedData}
                      getOptionLabel={(opt) => opt.name}
                      onSelect={(opt) => {
                        if (opt?.id === "add-vendor") {
                          handleVendorChange({ target: { value: "add-vendor" } });
                        } else {
                          handleVendorChange({ target: { value: opt?.id || "" } });
                          setSelectedVendorName(opt?.name || "");
                        }
                      }}
                      placeholder="Select Vendor"
                      value={selectedVendorId ? { id: selectedVendorId, name: selectedVendorName } : null}
                    />
                  </div>
                </div>
                <div className="product-form-row">
                  <div className="form-group product-field">
                    <input
                      type="number"
                      className="form-control-sales"
                      value={purchasePrice}
                      onChange={handlePurchasePriceChange}
                      onBlur={handlePurchasePriceBlur}
                      placeholder=""
                    />
                    <label className="floating-label">Purchase Price</label>
                    {purchasePriceError && <span className="error-text">{purchasePriceError}</span>}
                  </div>
                  <div className="form-group product-field">
                    <input
                      type="number"
                      className="form-control-sales"
                      value={sellingPrice}
                      onChange={handleSellingPriceChange}
                      onBlur={handleSellingPriceBlur}
                      placeholder=""
                    />
                    <label className="floating-label">Selling Price</label>
                    {sellingPriceError && <span className="error-text">{sellingPriceError}</span>}
                  </div>
                </div>
                <div className="product-form-row">
                  <div className="form-group product-field">
                    <input
                      type="text"
                      className="form-control-sales"
                      value={openingInventory}
                      onChange={handleOpeningInventoryChange}
                      placeholder=""
                      onFocus={handleFocusOpeningInventory}
                      onBlur={handleBlurOpeningInventory}
                    />
                    <label className="floating-label">Opening Inventory</label>
                  </div>
                  <div className="form-group product-field">
                    <input
                      type="number"
                      className="form-control-sales"
                      placeholder=""
                      value={totalPcInUom}
                      onChange={handleTotalPcInUomChange}
                    />
                    <label className="floating-label">Total Pc in Uom</label>
                  </div>
                </div>
                <div className="product-form-row">
                  <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={autoGenerateBarcode}
                      onChange={(e) => setAutoGenerateBarcode(e.target.checked)}
                    />
                    Auto Generate Barcode
                  </label>
                </div>
              </div>
            )}

            {!isNewProduct && (
              <div className="product-form-container">
                <div className="product-form-row">
                  <div className="form-group product-field">
                    <input
                      type="number"
                      className="form-control-sales"
                      value={purchasePrice || ""}
                      onChange={handlePurchasePriceChange}
                      placeholder=""
                    />
                    <label className="floating-label">Purchase Price</label>
                  </div>
                  <div className="form-group product-field">
                    <input
                      type="number"
                      className="form-control-sales"
                      value={sellingPrice || ""}
                      onChange={handleSellingPriceChange}
                      onBlur={handleSellingPriceBlur}
                      placeholder=""
                    />
                    <label className="floating-label">Selling Price</label>
                    {sellingPriceError && (
                      <span className="error-text">{sellingPriceError}</span>
                    )}
                  </div>
                </div>
                {/* UOM and Stock Row */}
                <div className="product-form-row">
                  <div className="form-group product-field">
                    <select
                      className="form-control-borderless"
                      value={
                        selectedSegment
                          ? `${selectedSegment.uom_title}_${selectedSegment.total_pc_in_uom}`
                          : ""
                      }
                      onChange={handleUomChange}
                      style={{
                        width: "220px",

                        color: selectedSegment === "" ? "black" : "#BABABA",  // gray if empty, black if filled
                        borderBottom: selectedSegment === "" ? "1px solid black" : "2px solid #ced4da",  // light border if empty, black if filled

                      }}


                     
                    >
                      <option value="" >Select UOM</option>
                      {segments?.length > 0 ? (
                        segments.map((segment) => (
                          <option
                            key={segment.id}
                            value={`${segment.uom_title}_${segment.total_pc_in_uom}`}
                          >
                            {segment.uom_title} ({segment.total_pc_in_uom} pc)
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No UOMs available
                        </option>
                      )}
                    </select>
                    {/* <label className="floating-label-option">Uom</label> */}
                  </div>
                  <div className="form-group product-field mt-2">
                    <input
                      type="number"
                      className="form-control-sales"
                      value={currentStock}
                      placeholder=""
                      disabled
                      style={{marginTop:"10px"}}
                    />
                    <label className="floating-label">Current Stock</label>
                  </div>
                </div>
                {/* Vendor and Total Pieces Row */}
                <div className="product-form-row">
                  <div className="form-group product-field">
                    <select
                      className="form-control-borderless product"
                      value={selectedVendorId || ""}
                      onChange={handleVendorChange}
                     style={{ width: "220px", color: selectedVendorId === "" ? "#bababa" : "black", borderBottom: '2px solid #ced4da' }}
                  >
                    
                      <option value="">Select Vendor</option>
                      <option value="add-vendor">Add Vendor</option>
                      {Array.isArray(vendors) &&
                        vendors
                          .filter(
                            (vendor) =>
                              vendor.type === "purchase" && !vendor.is_deleted
                          )
                          .map((vendor) => (
                            <option
                              key={vendor.id}
                              value={vendor.id}
                              selected={vendor.id === selectedVendorId}
                            >
                              {vendor.name}
                            </option>
                          ))}
                    </select>
                    {/* <label className="floating-label-option">Vendor</label> */}
                  </div>
                  <div className="form-group product-field mt-2">
                    <input
                      type="number"
                      className="form-control-sales"
                      value={
                        totalStockArrived *
                        (selectedSegment?.total_pc_in_uom || 1)
                      }
                      disabled
                      placeholder=""
                    />
                    <label className="floating-label">Total Piece Added</label>
                  </div>
                </div>
              </div>
            )}

            <div className="form-actions">
              <RegularButton
                text="Clear"
                onClick={resetForm}
                backgroundColor="#ffffff"
                color="#282E26"
              />

              <RegularButton
                text={isSubmitting ? "Saving..." : isNewProduct ? "Save Product" : "Save"}
                onClick={handleSubmit}
                backgroundColor="#282e26"
                color="#ffffff"
              />
              {/* <button onClick={handleSubmit} className="form-btn save-btn">
                {isNewProduct ? "Save Product" : "Save"}
              </button> */}
            </div>
          </div>
        </ReusableModal>

        <div className="invx-toolbar">
          <div className="invx-search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search product, category, UOM, vendor..."
              value={localSearch}
              onChange={(event) => setLocalSearch(event.target.value)}
            />
          </div>

          <div className="invx-summary-row">
            <MiniMetric icon={Package} label="Products" value={formatCount(totalProducts)} tone="blue" />
            <MiniMetric icon={Boxes} label="Total Stock" value={formatCount(totalInventoryUnits)} tone="green" />
            <MiniMetric icon={Wallet} label="Stock Value" value={`₹${formatMoney(totalInventoryValue)}`} tone="purple" />
            <MiniMetric icon={AlertTriangle} label="Low / Empty" value={`${formatCount(lowStockCount)} / ${formatCount(noStockCount)}`} tone="amber" />
          </div>
        </div>

        <div className="invx-product-list-panel">
          <div className="invx-list-header">
            <div>
              <h2>Product Wise Inventory</h2>
              <p>
                Grouped by product name. Expand any product to view UOM, prices, margin and stock history.
              </p>
            </div>

            <span className="invx-category-pill">
              {formatCount(totalCategories)} Categories
            </span>
          </div>

          {isInventoryLoading ? (
            <div className="invx-loading-state">
              Loading inventory...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="invx-empty-state">
              No matching inventory found.
            </div>
          ) : (
            <div className="invx-accordion-list">
              {filteredProducts.map((product) => (
                <ProductAccordionRow
                  key={product.key}
                  group={product}
                  expanded={expandedProductKey === product.key}
                  onToggle={() =>
                    setExpandedProductKey((current) =>
                      current === product.key ? null : product.key
                    )
                  }
                />
              ))}
            </div>
          )}

          <div className="invx-footer-bar">
            <div>
              Showing {formatCount(filteredProducts.length)} grouped products
              {totalCount ? ` from ${formatCount(totalCount)} stock records` : ""}
            </div>

            <div className="invx-pagination">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </button>
              <span>Page {currentPage}</span>
              <button
                type="button"
                disabled={inventoryData.length === 0}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      <AddVendorModal
        show={showVendorModal}
        handleClose={handleCloseVendorModal}
        onVendorAdded={handleVendorAdded}
        updateVendors={updateVendors}
      />
    </>
  );
};


const inventoryGroupedStyles = `
  .invx-page {
    color: #0f172a;
  }

  .inventory-header {
    align-items: center;
    border-bottom: 1px solid #e8edf3;
    padding-bottom: 16px;
    margin-bottom: 18px;
  }

  .inventory-title {
    font-size: 27px;
    font-weight: 950;
    letter-spacing: -0.04em;
    color: #17201a;
  }

  .invx-toolbar {
    display: grid;
    grid-template-columns: minmax(280px, 420px) minmax(0, 1fr);
    gap: 14px;
    align-items: stretch;
    margin: 14px 0 18px;
  }

  .invx-search-box {
    min-height: 50px;
    border: 1px solid #d7dee8;
    border-radius: 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    background: #ffffff;
    padding: 0 15px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.045);
  }

  .invx-search-box svg {
    color: #64748b;
    flex-shrink: 0;
  }

  .invx-search-box input {
    border: none;
    outline: none;
    width: 100%;
    color: #0f172a;
    font-size: 14px;
    font-weight: 750;
    background: transparent;
  }

  .invx-search-box input::placeholder {
    color: #94a3b8;
  }

  .invx-summary-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
  }

  .invx-mini-metric {
    min-height: 50px;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 11px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.045);
  }

  .invx-mini-icon {
    width: 34px;
    height: 34px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .invx-mini-metric strong {
    display: block;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
    line-height: 1.1;
  }

  .invx-mini-metric p {
    margin: 2px 0 0;
    color: #64748b;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .invx-mini-metric.blue .invx-mini-icon {
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }

  .invx-mini-metric.green .invx-mini-icon {
    color: #059669;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .invx-mini-metric.purple .invx-mini-icon {
    color: #7c3aed;
    background: #f3e8ff;
    border: 1px solid #ddd6fe;
  }

  .invx-mini-metric.amber .invx-mini-icon {
    color: #d97706;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }

  .invx-product-list-panel {
    border: 1px solid #dbe3ec;
    border-radius: 22px;
    background: #ffffff;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.055);
    overflow: hidden;
  }

  .invx-list-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 18px 18px 14px;
    border-bottom: 1px solid #edf2f7;
    background:
      radial-gradient(circle at top left, rgba(13, 83, 47, 0.08), transparent 32%),
      linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  }

  .invx-list-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: 19px;
    font-weight: 950;
    letter-spacing: -0.035em;
  }

  .invx-list-header p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .invx-category-pill {
    display: inline-flex;
    align-items: center;
    height: 30px;
    border-radius: 999px;
    padding: 0 12px;
    color: #0d532f;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .invx-accordion-list {
    display: grid;
    gap: 0;
  }

  .invx-product-row {
    border-bottom: 1px solid #edf2f7;
  }

  .invx-product-row:last-child {
    border-bottom: none;
  }

  .invx-product-main {
    width: 100%;
    min-height: 76px;
    border: none;
    background: #ffffff;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 16px;
    align-items: center;
    padding: 14px 18px;
    text-align: left;
    cursor: pointer;
    transition: background 0.16s ease;
  }

  .invx-product-main:hover {
    background: #fbfdff;
  }

  .invx-product-row.expanded .invx-product-main {
    background: #f8fafc;
  }

  .invx-product-left {
    display: flex;
    align-items: center;
    gap: 13px;
    min-width: 0;
  }

  .invx-product-avatar {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #dbe3ec;
    color: #0d532f;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
  }

  .invx-product-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .invx-product-avatar.has-image {
    background: #ffffff;
    border-color: #cbd5e1;
  }

  .invx-product-avatar.has-image .invx-placeholder-icon {
    display: none;
  }

  .invx-placeholder-icon {
    color: #0d532f;
  }

  .invx-product-text {
    min-width: 0;
  }

  .invx-product-text strong {
    display: block;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .invx-product-text span {
    display: block;
    margin-top: 4px;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .invx-product-metrics {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-shrink: 0;
  }

  .invx-product-metrics > span:not(.invx-stock-badge) {
    display: grid;
    gap: 2px;
    text-align: right;
  }

  .invx-product-metrics small {
    color: #64748b;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .invx-product-metrics b {
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
  }

  .invx-stock-badge {
    min-width: 76px;
    height: 28px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 950;
  }

  .invx-stock-badge.success {
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .invx-stock-badge.info {
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }

  .invx-stock-badge.warning {
    color: #b45309;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }

  .invx-stock-badge.danger {
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
  }

  .invx-chevron {
    color: #64748b;
    transition: transform 0.16s ease;
  }

  .invx-product-row.expanded .invx-chevron {
    transform: rotate(180deg);
  }

  .invx-expanded-area {
    padding: 0 18px 18px 77px;
    background: #f8fafc;
  }

  .invx-expanded-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(320px, 0.75fr);
    gap: 12px;
  }

  .invx-detail-card {
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #ffffff;
    padding: 13px;
  }

  .invx-detail-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
    margin-bottom: 10px;
  }

  .invx-detail-title svg {
    color: #0d532f;
  }

  .invx-variant-table-wrap {
    overflow-x: auto;
  }

  .invx-variant-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .invx-variant-table th {
    padding: 9px 10px;
    color: #64748b;
    background: #f8fafc;
    text-align: left;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  .invx-variant-table td {
    padding: 10px;
    color: #334155;
    font-weight: 750;
    border-bottom: 1px solid #f1f5f9;
    white-space: nowrap;
  }

  .invx-qty {
    color: #0d532f;
    font-weight: 950;
  }

  .invx-positive {
    color: #059669 !important;
    font-weight: 950 !important;
  }

  .invx-negative {
    color: #dc2626 !important;
    font-weight: 950 !important;
  }

  .invx-history-list {
    display: grid;
    gap: 8px;
  }

  .invx-history-item {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 10px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #edf2f7;
  }

  .invx-history-item strong {
    display: block;
    color: #0f172a;
    font-size: 12px;
    font-weight: 950;
  }

  .invx-history-item span {
    display: block;
    margin-top: 3px;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .invx-history-item b {
    color: #0d532f;
    font-size: 13px;
    font-weight: 950;
    white-space: nowrap;
  }

  .invx-empty-history,
  .invx-empty-state,
  .invx-loading-state {
    min-height: 92px;
    border: 1px dashed #cbd5e1;
    border-radius: 14px;
    background: #f8fafc;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 18px;
    font-size: 13px;
    font-weight: 850;
  }

  .invx-footer-bar {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    padding: 13px 18px;
    border-top: 1px solid #edf2f7;
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
    background: #fbfdff;
  }

  .invx-pagination {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .invx-pagination button,

  @media (max-width: 1200px) {
    .invx-toolbar {
      grid-template-columns: 1fr;
    }

    .invx-summary-row {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .invx-expanded-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 760px) {
    .invx-product-main {
      grid-template-columns: 1fr;
    }

    .invx-product-metrics {
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .invx-expanded-area {
      padding-left: 18px;
    }

    .invx-footer-bar {
      align-items: flex-start;
      flex-direction: column;
    }

    .btn-position {
      flex-wrap: wrap;
    }
  }

  @media (max-width: 540px) {
    .invx-summary-row {
      grid-template-columns: 1fr;
    }

    .inventory-header {
      align-items: flex-start;
      flex-direction: column;
      gap: 12px;
    }
  }
`;


export default Inventory;
